// Outline of the system
//
// This framework operates on the principle of explicit, structured and centralised state. A single object, belonging to the Game instance, forms a tree containing everything that is necessary knowledge about the game. The data in this state tree is restricted to the set of javascript that can be serialised to JSON. The structure of the state tree can only be modified by an Instruction.
// 
// What is an Instruction?
//
// An instruction is just a recipe for modifying the structure of the game's state in some way. This means adding, subtracting or modifying nodes from the state tree. Instructions are instantiated with a reference to their parent and to the game object. Loading or unloading are the only phases when an instruction can modify the game state's structure. Instructions can be nested, which means that an instruction can add a child instruction to itself. The child must be appended to the .children member variable.
//
// State structure, state configurations and auxiliary state
//
// There is a distinction between the structure of the state and the particular values the nodes in the tree have been assigned. To be specific, there is a surjective mapping from the set of possible loaded instructions to the set of possible structures, but not necessarily to the values assigned to them. The intent of this is to ensure that no matter what has happened to the game, the same state structure can be recovered by instantiating and loading the correct set of instructions. To enable this, there is a set of rules when it comes to using instructions.
//
//   1. Structure is only modified when the instruction is loaded or unloaded.
//   2. Loading and unloading preserves the loaded instructions -> structure relation. No matter how the loading pattern was achieved, it should always create the same structure once completed.
//   3. Anything that is done outside of the state structure while the instruction is loading/loaded must be reverted on unloading.
//
// This is most easily achievable with a tree model matching that of the instructions themselves. Each loaded instruction adds a node to the state tree. When unloaded, it deletes it. Children add children nodes to their parent's node. This implies that a node cannot be unloaded before its children are. Other schemes can be used but care must be taken to adhere to rule 2.
//
// State configurations represent the specific set of values a structure has been assigned. The configuration contains every piece of information that needs to be known about the game to recover it to a satisfactorily similar state. Auxiliary state, on the other hand, is allowed to contain state that is not important e.g. exact frame in the walk cycle of a character or the position of every particle in a particle effect. It may also contain state that is important but would be cumbersome or impossible to serialise e.g. instantiated objects, objects from 3rd party libraries or the location of every vertex in a rigged mesh. Auxiliary state encompasses everything that is changed by an instruction on load or unload outside of the state tree.
//
// In order to make the state configuration useful, then, two functions are defined on every instruction: updateState and updateAuxiliary. updateState interprets the important but unserialisable information and updates the configuration accordingly. updateAuxiliary takes the state configuration and establishes a corresponding auxiliary configuration. Calling updateState then updateAuxiliary should produce no change in the important auxiliary state. At any point in the game, if every instruction was unloaded then reloaded, state configuration restored from backup, and updateAuxiliary called, the game should still function and nothing of consequence should be different from when the state configuration was recorded. Auxiliary state added when loading an instruction must be deleted when an instruction is unloaded, as long as the difference between the current game and the game loaded from save is of no consequence.
//
// Groups
//
// Groups are somewhat orthogonal to the instruction system and at the moment are simply a way to access functionality provided by the game instance. At the moment, this includes attaching functions to the rendering loop and the update loop in a structured way.

import Assets from './assets';
import MessageBus from './messagebus';
import Movements from './movements';

export default class Game {
    constructor () {
        this.logic = {};
        this.gamewindows = {};
        this.unnamedlogic = [];
        this.unnamedgamewindows = [];
        this.messagebus = new MessageBus();
        this.filterbusses = {};
        
        // Keys of active groups
        this.groupnames = [];

        this.instructions = {};

        this.movements = new Movements(this);

        this.updateTimestep = 30;

        this.velIterations = 10;
        this.posIterations = 3;

        this.startTime = (new Date()).getTime();
        this.lastUpdateTime = 0;

        window.onresize = this.onResize.bind(this);

        window.ontouchstart = this.onTouchStart.bind(this);
        window.ontouchend = this.onTouchEnd.bind(this);
        window.onclick = this.onClick.bind(this);
        window.onmousemove = this.onMouseMove.bind(this);
        window.onkeydown = this.onKeyDown.bind(this);
        window.onkeyup = this.onKeyUp.bind(this);

        this.focussed = true;
        window.onfocus = (function (ev) {
            this.focussed = true;
            this.lastUpdateTime = (new Date()).getTime();
        }).bind(this);

        window.onblur = (function (ev) {
            this.focussed = false;
        }).bind(this);

        window.onresize = this.onResize.bind(this);

        this.assets = new Assets(this._start.bind(this));
    }

    _start () {
        if (this._readyCallbacks) {
            for (var i = 0; i < this._readyCallbacks.length; ++i) {
                this._readyCallbacks[i]();
            }
        }

        // ALL ABOARD
        this.render();
        this.update();
    }

    ready (callback) {
        if (!this._readyCallbacks) this._readyCallbacks = [];

        this._readyCallbacks.push(callback);

        return this;
    }

    render () {
        var gamewindow;
        for (var key in this.gamewindows) {
            var gamewindowgroup = this.gamewindows[key];
            for (var gw = 0; gw < gamewindowgroup.length; ++gw) {
                gamewindow = gamewindowgroup[gw];
                if (gamewindow.finish) {
                    gamewindowgroup.splice(gw, 1);
                    --gw;
                } else {
                    gamewindow.render();
                }
            }
        }

        for (var gw = 0; gw < this.unnamedgamewindows.length; ++gw) {
            gamewindow = this.unnamedgamewindows[gw];
            if (gamewindow.finished) {
                this.unnamedgamewindows.splice(gw, 1);
                --gw;
            } else {
                gamewindow.render();
            }
        }
        
        requestAnimationFrame(this.render.bind(this));
    }

    update () {
        if (!this.focussed) {
            setTimeout(this.update.bind(this), this.updateTimestep);
            return;
        }

        var newtime = (new Date()).getTime() - this.startTime;
        var delta = newtime - this.lastUpdateTime;
        this.lastUpdateTime = newtime;

        var logic;
        for (var key in this.logic) {
            var logicgroup = this.logic[key];
            for (var l = 0; l < logicgroup.length; ++l) {
                logic = logicgroup[l];
                if (logic.finished) {
                    logicgroup.splice(l, 1);
                    --l;
                } else {
                    logic.update(delta, newtime);
                }
            }
        }

        for (var l = 0; l < this.unnamedlogic.length; ++l) {
            logic = this.unnamedlogic[l];
            if (logic.finished) {
                this.unnamedlogic.splice(l,1);
                --l;
            } else {
                logic.update(delta, newtime);
            }
        }

        setTimeout(this.update.bind(this), this.updateTimestep);
    }

    load (instruction) {
        this.instructions[instruction.name] = instruction;
        instruction.attachToGame(this);
        instruction.load();
    }

    unload (name) {
        this.instructions[name].unload();
        this.instructions[name].detachFromGame();
        delete this.instructions[name];
    }

    addLogic (logic, name) {
        if (name) {
            if (!this.logic.hasOwnProperty(name)) this.logic[name] = [];
            this.logic[name].push(logic);
        } else {
            this.unnamedlogic.push(logic);
        }
    }

    addGamewindow (gamewindow, name) {
        if (name) {
            if (!this.gamewindows.hasOwnProperty(name)) this.gamewindows[name] = [];
            this.gamewindows[name].push(gamewindow);
        } else {
            this.unnamedgamewindows.push(gamewindow);
        }
    }

    addFilterbus (filterbus, name) {
        this.filterbusses[name] = filterbus;

        for (var channel in filterbus.filter) {
            this.messagebus.registerOnChannel(channel, filterbus);
        }
    }

    removeLogic (name) {
        delete this.logic[name];
    }

    removeGamewindows (name) {
        delete this.gamewindows[name];
    }

    removeFilterbus (name) {
        this.messagebus.unsubscribe(this.filterbusses[name]);

        delete this.filterbusses[name];
    }

    onResize () {
        for (var gw = 0; gw < this.gamewindows.length; ++gw) {
            this.gamewindows[gw].resize(window.innerWidth, window.innerHeight);
        }

        this.messagebus.sendMessage("resize");
    }

    onTouchStart (touchevent) {
        this.messagebus.sendMessage("touchstart", touchevent);
    }

    onTouchEnd (touchevent) {
        this.messagebus.sendMessage("touchend", touchevent);
    }

    onClick (mouseevent) {
        this.messagebus.sendMessage("click", mouseevent);
    }

    onMouseMove (mouseevent) {
        this.messagebus.sendMessage("mousemove", mouseevent);
    }

    onKeyDown (keyevent) {
        this.messagebus.sendMessage("keydown", keyevent);
    }

    onKeyUp (keyevent) {
        this.messagebus.sendMessage("keyup", keyevent);
    }
}
